import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import MobileShell from "@/components/MobileShell";
import {
  ArrowLeft,
  FileText,
  Pill,
  FlaskConical,
  FileBadge2,
  Receipt,
  Share2,
  Activity,
  ChevronRight,
  User,
  Camera,
  Phone,
  MapPin,
  CalendarDays,
  ClipboardList,
  Stethoscope,
  Paperclip,
  History,
  AlertTriangle,
} from "lucide-react";

function CardButton({ icon: Icon, label, sub, tone = "blue", onClick }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    purple: "bg-purple-50 text-purple-700",
    red: "bg-red-50 text-red-700",
    slate: "bg-slate-100 text-slate-700",
  };

  return (
    <button
      onClick={onClick}
      className="w-full rounded-2xl bg-white border border-slate-200 p-4 text-left flex items-center justify-between active:scale-[0.99] shadow-sm"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${tones[tone] || tones.blue}`}>
          <Icon size={21} />
        </div>
        <div className="min-w-0">
          <div className="font-bold text-slate-900">{label}</div>
          {sub ? <div className="text-xs text-slate-500 mt-0.5 truncate">{sub}</div> : null}
        </div>
      </div>
      <ChevronRight size={18} className="text-slate-400 shrink-0" />
    </button>
  );
}

function fmtDate(v) {
  if (!v) return "";
  return String(v).slice(0, 10);
}

export default function DoctorMobilePatient() {
  const { id } = useParams();
  const nav = useNavigate();

  const [patient, setPatient] = useState(null);
  const [latestVitals, setLatestVitals] = useState(null);
  const [latestRecord, setLatestRecord] = useState(null);
  const [counts, setCounts] = useState({
    records: 0,
    prescriptions: 0,
    labs: 0,
    certificates: 0,
    billing: 0,
    referrals: 0,
    vitals: 0,
    notes: 0,
  });
  const [loading, setLoading] = useState(true);
  const [photoBusy, setPhotoBusy] = useState(false);

  const uploadPhoto = async (fileObj) => {
    if (!fileObj) return;
    if (!fileObj.type?.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const { data } = await api.post(`/patients/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPatient((p) => ({ ...p, photo_url: data.photo_url }));
      toast.success("Patient photo saved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setPhotoBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, records, rx, labs, certs, refs, vitals, tx, notes] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get(`/patient-records/patient/${id}`).catch(() => ({ data: [] })),
        api.get(`/prescriptions?patient_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/lab-results`).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/medical-certificates`).catch(() => ({ data: [] })),
        api.get(`/referrals?patient_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/vitals`).catch(() => ({ data: [] })),
        api.get(`/pos/transactions`, { params: { scope: "all" } }).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/clinical-notes`).catch(() => ({ data: [] })),
      ]);

      const allRx = Array.isArray(rx.data) ? rx.data : [];
      const allRefs = Array.isArray(refs.data) ? refs.data : [];
      const allTx = Array.isArray(tx.data) ? tx.data : [];
      const recordList = Array.isArray(records.data) ? records.data : [];
      const vitalsList = Array.isArray(vitals.data) ? vitals.data : [];
      const notesList = Array.isArray(notes.data) ? notes.data : [];

      setPatient(p.data);
      setLatestVitals(vitalsList[0] || null);
      setLatestRecord(recordList[0] || null);

      setCounts({
        records: recordList.length,
        prescriptions: allRx.filter((x) => x.patient_id === id && x.status !== "deleted").length,
        labs: Array.isArray(labs.data) ? labs.data.length : 0,
        certificates: Array.isArray(certs.data) ? certs.data.length : 0,
        billing: allTx.filter((x) => x.patient_id === id).length,
        referrals: allRefs.filter((x) => x.patient_id === id).length,
        vitals: vitalsList.length,
        notes: notesList.length,
      });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 p-5">
        <div className="text-slate-500">Loading patient...</div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-slate-100 p-5">
        <button onClick={() => nav(-1)} className="mb-4 text-blue-700 font-bold">Back</button>
        <div className="text-slate-500">Patient not found.</div>
      </div>
    );
  }

  const demographics = [
    patient.age ? `${patient.age} y/o` : "",
    patient.gender || "",
    patient.patient_number || "",
  ].filter(Boolean).join(" · ");

  const v = latestVitals?.vitals || {};

  return (
    <MobileShell>
      {/* Page header (back + name) */}
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-4 flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          className="w-11 h-11 rounded-2xl bg-slate-100 flex items-center justify-center active:scale-95 transition shrink-0"
          aria-label="Back"
          data-testid="patient-back"
        >
          <ArrowLeft size={20} className="text-slate-700" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-xl font-extrabold text-slate-900 leading-tight truncate" style={{ fontFamily: "Work Sans" }}>
            {patient.full_name}
          </div>
          <div className="text-sm text-slate-500">Patient Summary</div>
        </div>
      </div>

      <div className="p-4">
        <div className="rounded-3xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white p-5 mb-4 shadow-lg">
          <label className="relative w-16 h-16 rounded-2xl bg-white/15 overflow-hidden flex items-center justify-center mb-3 active:scale-95 transition cursor-pointer" data-testid="patient-photo">
            {patient.photo_url ? (
              <img src={patient.photo_url} alt={patient.full_name || "patient"} className="w-full h-full object-cover" />
            ) : (
              <User size={26} />
            )}
            <span className="absolute bottom-0 inset-x-0 bg-black/40 flex items-center justify-center py-0.5">
              <Camera size={13} className="text-white" />
            </span>
            <input type="file" accept="image/*" className="hidden" disabled={photoBusy} onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} data-testid="patient-photo-input" />
          </label>

          <div className="text-2xl font-black leading-tight">{patient.full_name}</div>
          <div className="text-sm text-blue-50 mt-1">{demographics || "No demographics"}</div>

          <div className="grid grid-cols-1 gap-2 mt-4 text-sm">
            {patient.contact_number ? (
              <div className="flex items-center gap-2 text-blue-50">
                <Phone size={14} />
                {patient.contact_number}
              </div>
            ) : null}

            {patient.address ? (
              <div className="flex items-center gap-2 text-blue-50">
                <MapPin size={14} />
                {patient.address}
              </div>
            ) : null}

            {patient.birthdate ? (
              <div className="flex items-center gap-2 text-blue-50">
                <CalendarDays size={14} />
                DOB: {fmtDate(patient.birthdate)}
              </div>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-2xl bg-white p-4 border border-slate-200 shadow-sm">
            <div className="text-xs text-slate-500">Vitals</div>
            <div className="text-lg font-black text-slate-900 mt-1">
              {v.blood_pressure || "—"}
            </div>
            <div className="text-xs text-slate-400 mt-1">Latest BP</div>
          </div>

          <div className="rounded-2xl bg-white p-4 border border-slate-200 shadow-sm">
            <div className="text-xs text-slate-500">Records</div>
            <div className="text-lg font-black text-slate-900 mt-1">{counts.records}</div>
            <div className="text-xs text-slate-400 mt-1">Total entries</div>
          </div>
        </div>

        {latestRecord ? (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-700 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-bold text-amber-900">Latest Record</div>
                <div className="text-sm text-amber-800 mt-1 truncate">{latestRecord.title || latestRecord.type}</div>
                <div className="text-xs text-amber-700 mt-1">{fmtDate(latestRecord.date || latestRecord.created_at)}</div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="mb-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Today&apos;s Work</div>
        </div>

        <div className="space-y-3">
          <CardButton
            icon={Activity}
            label="Vitals"
            sub={`${counts.vitals} entries`}
            tone="emerald"
            onClick={() => nav(`/patients/${id}`)}
          />

          <CardButton
            icon={Stethoscope}
            label="SOAP / Clinical Notes"
            sub={`${counts.notes} notes`}
            tone="blue"
            onClick={() => nav(`/patients/${id}`)}
          />

          <CardButton
            icon={Pill}
            label="Prescription"
            sub={`${counts.prescriptions} prescriptions`}
            tone="emerald"
            onClick={() => nav(`/patients/${id}`)}
          />

          <CardButton
            icon={FileBadge2}
            label="Medical Certificate"
            sub={`${counts.certificates} certificates`}
            tone="purple"
            onClick={() => nav(`/patients/${id}`)}
          />

          <CardButton
            icon={Share2}
            label="Referral"
            sub={`${counts.referrals} referrals`}
            tone="amber"
            onClick={() => nav(`/patients/${id}`)}
          />

          <CardButton
            icon={FlaskConical}
            label="Laboratory"
            sub={`${counts.labs} lab records`}
            tone="red"
            onClick={() => nav(`/patients/${id}`)}
          />

          <CardButton
            icon={Receipt}
            label="Billing / POS"
            sub={`${counts.billing} transactions`}
            tone="slate"
            onClick={() => nav(`/patients/${id}`)}
          />

          <CardButton
            icon={Paperclip}
            label="Attachments"
            sub="Documents, files, images"
            tone="slate"
            onClick={() => nav(`/patients/${id}`)}
          />

          <CardButton
            icon={History}
            label="Timeline / History"
            sub={`${counts.records + counts.prescriptions + counts.labs + counts.certificates} clinical events`}
            tone="blue"
            onClick={() => nav(`/patients/${id}`)}
          />

          <CardButton
            icon={FileText}
            label="Open Full Desktop Chart"
            sub="Use this when more detail is needed"
            tone="slate"
            onClick={() => nav(`/patients/${id}`)}
          />
        </div>
      </div>
    </MobileShell>
  );
}
